import React from 'react';
import { Link } from 'react-router-dom/dist/index.js';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const sections = [
  {
    id: 1,
    title: "Hoe werkt Verduurzaamwijzer.nu?",
    content: (
      <div className="space-y-4">
        <p>
          Verduurzaamwijzer.nu is een onafhankelijk platform dat huiseigenaren helpt bij het maken van de juiste
          keuze voor woningverduurzaming. Ons proces is eenvoudig, transparant en volledig gericht op uw belang.
        </p>
        <p>
          Van eerste oriëntatie tot het ontvangen van offertes: wij begeleiden u door elke stap van het proces.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    title: "Stap 1 – Oriëntatie en informatie",
    content: (
      <div className="space-y-4">
        <p>
          Alles begint met kennis. Op onze kennisbank vindt u uitgebreide, onafhankelijke informatie over alle
          verduurzamingsoplossingen: isolatie, warmtepompen, zonnepanelen, thuisbatterijen en ventilatie.
        </p>
        <p>
          Onze content wordt geschreven door vakspecialisten en regelmatig bijgewerkt op basis van de nieuwste
          inzichten, wet- en regelgeving en technologische ontwikkelingen.
        </p>
        <ul className="space-y-2 list-none">
          {[
            "Vergelijkingen van technologieën en systemen",
            "Uitleg over subsidies en financieringsmogelijkheden",
            "Praktische gidsen voor elke woningsituatie",
            "Veelgestelde vragen en veelgemaakte fouten",
          ]?.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <Icon name="BookOpen" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 3,
    title: "Stap 2 – Persoonlijk advies",
    content: (
      <div className="space-y-4">
        <p>
          Via ons platform kunt u een persoonlijk adviesaanvraag indienen. U vult een kort formulier in over uw
          woning, uw situatie en uw wensen. Op basis hiervan bepalen wij welke oplossingen het meest geschikt
          zijn voor uw specifieke situatie.
        </p>
        <p>
          Ons advies is altijd gebaseerd op uw belang — niet op het belang van een aanbieder. Wij verdienen
          geen commissie op het advies zelf.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    title: "Stap 3 – Aanbiedersselectie",
    content: (
      <div className="space-y-4">
        <p>
          Wij werken uitsluitend samen met gecertificeerde en gescreende aanbieders. Elke aanbieder op ons
          platform heeft een uitgebreide toelatingsprocedure doorlopen:
        </p>
        <ul className="space-y-2 list-none">
          {[
            ["Certificaatcontrole", "Verificatie van alle vereiste certificeringen (BRL, STEK, etc.)"],
            ["Referentiecheck", "Controle van referenties en eerdere projecten"],
            ["Reviewanalyse", "Analyse van klantbeoordelingen op meerdere platforms"],
            ["Contractuele afspraken", "Aanbieders ondertekenen onze gedragscode"],
            ["Periodieke herbeoordeling", "Aanbieders worden jaarlijks opnieuw beoordeeld"],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 5,
    title: "Stap 4 – Offertes ontvangen",
    content: (
      <div className="space-y-4">
        <p>
          Op basis van uw aanvraag selecteren wij maximaal drie passende aanbieders in uw regio. Zij nemen
          contact met u op voor een vrijblijvende offerte. U bent nooit verplicht om een offerte te accepteren.
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
          <strong>Gratis en vrijblijvend:</strong> Het aanvragen van offertes via Verduurzaamwijzer.nu is
          altijd gratis voor huiseigenaren. U betaalt niets en zit nergens aan vast.
        </div>
      </div>
    ),
  },
  {
    id: 6,
    title: "Stap 5 – Nazorg en reviews",
    content: (
      <div className="space-y-4">
        <p>
          Na afronding van uw project vragen wij u om een beoordeling van de aanbieder. Deze reviews zijn
          onbewerkt en worden gepubliceerd op het profiel van de aanbieder. Aanbieders kunnen reviews niet
          laten verwijderen.
        </p>
        <p>
          Heeft u een klacht over een aanbieder? Wij bemiddelen kosteloos en kunnen een aanbieder van ons
          platform verwijderen als de kwaliteitsnormen niet worden nageleefd.
        </p>
      </div>
    ),
  },
  {
    id: 7,
    title: "Kwaliteitsborging",
    content: (
      <div className="space-y-4">
        <p>
          Wij monitoren continu de kwaliteit van aanbieders op ons platform. Bij structurele klachten of
          het niet nakomen van afspraken wordt een aanbieder gesuspendeerd of verwijderd, ongeacht de
          commerciële relatie.
        </p>
        <p>
          Meer informatie over onze onafhankelijkheid en financiering vindt u op de{' '}
          <Link to="/onafhankelijkheid" className="text-primary hover:underline font-medium">Onafhankelijkheid-pagina</Link>.
        </p>
      </div>
    ),
  },
];

const WerkwijzePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/8 via-background to-background pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-foreground/50 mb-4">
              <Link to="/homepage" className="hover:text-primary transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <Link to="/over-ons" className="hover:text-primary transition-colors">Over Ons</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground/70">Werkwijze</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Settings" size={20} className="text-primary" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">Werkwijze</h1>
            </div>
            <p className="text-foreground/70 text-lg leading-relaxed max-w-2xl">
              Van oriëntatie tot offerte: zo helpt Verduurzaamwijzer.nu u stap voor stap naar de juiste keuze.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="HelpCircle" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Vragen over onze werkwijze?</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Neem contact op via{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>{' '}
                      of bel{' '}
                      <a href="tel:0647650537" className="text-primary hover:underline font-medium">
                        06 4765 0537
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WerkwijzePage;
